import { lazy, Suspense, useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Radio } from 'lucide-react';

import {
  Button,
  Card,
  CardContent,
  UserAvatar,
} from '@/components/ui';
import { cn } from '@/lib/utils';
import { EVENTS, track } from '@/lib/analytics';
import { useCounts } from '@/lib/api/hooks/useCounts';
import { useScrollTrack } from '@/lib/useScrollTrack';
import { useAuth } from '@/pages/auth';

// Lazy-load the subscription modal — same pattern as `UserMenu` so the
// PayPal/Cashfree SDK chunks stay off the dashboard's critical path.
const SubscriptionDialog = lazy(() =>
  import('@/pages/subscription').then((m) => ({ default: m.SubscriptionDialog })),
);

import { dashboardNav } from './nav-items';
import { NavBadge } from './_shared';
import { userInitials } from './utils';

/** Defensive read of the (loosely typed) subscription block on `User`. */
interface SubscriptionRef {
  allowed?: boolean;
  plan?: string;
  name?: string;
  details?: {
    plan_name?: string;
    status?: string;
    status_text?: string;
    show_date?: string;
    formated_price?: string;
  };
}
function readSubscription(value: unknown): SubscriptionRef | null {
  if (value && typeof value === 'object') return value as SubscriptionRef;
  return null;
}

/** Plan-tier emoji badge — mirrors `UserAvatar`'s PLAN_BADGE map. */
const PLAN_BADGES: Record<string, string> = {
  pro: '🔥',
  premium: '👑',
};

/**
 * Left dashboard rail: user card, primary navigation, optional subscription
 * card, and small legal links. Hidden below `lg`.
 */
export function DashboardSidebar() {
  const user = useAuth((s) => s.user);
  const { data: counts } = useCounts();
  const subscription = readSubscription(user?.subscription);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);

  function handleOpenSubscription() {
    track(EVENTS.SUBSCRIPTION_MODAL_SHOWN, {
      trigger: 'manual',
      source: 'sidebar_plan_card',
    });
    setSubscriptionOpen(true);
  }

  // Bidirectional scroll-following — only attaches its listener when
  // the sidebar is actually rendered (≥ lg). On mobile the wrapper is
  // CSS-hidden but the hook would still fire on every scroll otherwise.
  const isLg =
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(min-width: 1024px)').matches
      : true;
  const [enabled, setEnabled] = useState(isLg);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setEnabled(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  const { slotRef, innerRef, tracking, style } = useScrollTrack({ enabled });

  return (
    <aside
      ref={slotRef}
      // Slot stays in flow so the main column doesn't shift horizontally
      // when the sidebar is pinned. Self-start keeps it at the top of the
      // flex cell.
      className="hidden w-[210px] shrink-0 self-start lg:block"
    >
      <div
        ref={innerRef}
        className={cn(
          'py-6 pr-2',
          // When tracking, `position: fixed` — `top`/`left`/`width` come
          // from `style` (set by `useScrollTrack`). `py-6` (already on)
          // gives a 1.5rem margin off the Topbar and viewport bottom,
          // matching the right rails.
          tracking && 'fixed z-30',
        )}
        style={style}
      >
      {/* User card / sign-in prompt */}
      {user ? (
        <Card className="mb-4">
          <CardContent className="flex flex-col items-center gap-2 p-5 text-center">
            <UserAvatar
              src={user.image}
              alt={`${user.firstname} ${user.lastname}`}
              fallback={userInitials(user)}
              plan={user.plan}
              ring
              className="h-16 w-16"
            />
            <div className="w-full min-w-0">
              <p
                className="line-clamp-2 break-words font-display text-base text-ink"
                title={`${user.firstname ?? ''} ${user.lastname ?? ''}`.trim()}
              >
                {`${user.firstname ?? ''} ${user.lastname ?? ''}`.trim()}
              </p>
              {user.username ? (
                <Link
                  to={`/user/${user.username}`}
                  className="block truncate text-sm text-brand-700 hover:underline"
                  title={`@${user.username}`}
                >
                  @{user.username}
                </Link>
              ) : null}
            </div>
            <Button asChild className="mt-1 w-full rounded-full">
              <Link to="/dashboard/livestream">
                <Radio className="h-4 w-4" />
                Go Live
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-4">
          <CardContent className="flex flex-col items-center gap-3 p-5 text-center">
            <p className="text-sm text-muted-foreground">
              Sign in to see your space
            </p>
            <Button asChild className="w-full rounded-full">
              <Link to="/login">Sign in</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Subscription mini-card — moved above the menu 2026-05-31; reskinned
          2026-06-05 with plan badge + status pill + renewal meta + full-width
          Manage CTA. Sits right under the user card. */}
      {subscription?.allowed ? (() => {
        const d = subscription.details ?? {};
        const planName =
          d.plan_name ?? subscription.name ?? subscription.plan ?? user?.plan ?? 'Pro';
        const planTier = (user?.plan ?? '').toLowerCase();
        const badge = PLAN_BADGES[planTier];
        const status = (d.status ?? 'active').toLowerCase();
        const statusLabel =
          d.status_text ??
          (status === 'active'
            ? 'Active'
            : status === 'cancelled'
              ? 'Cancelled'
              : status === 'pending'
                ? 'Pending'
                : status);
        const statusClass =
          status === 'active'
            ? 'bg-emerald-100 text-emerald-700'
            : status === 'cancelled'
              ? 'bg-rose-100 text-rose-700'
              : 'bg-amber-100 text-amber-700';
        return (
          <Card className="mb-4 overflow-hidden border-brand-200 bg-gradient-to-br from-brand-50 to-brand-100/70 shadow-sm">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-brand-700/80">
                    Your plan
                  </p>
                  <p className="mt-0.5 truncate font-display text-base font-medium text-ink">
                    {badge ? <span aria-hidden>{badge} </span> : null}
                    {planName}
                  </p>
                </div>
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                    statusClass,
                  )}
                >
                  {statusLabel}
                </span>
              </div>

              {d.show_date ? (
                <p className="-mt-2.5 text-xs leading-snug text-muted-foreground">
                  {status === 'cancelled' ? 'Ends ' : 'Renews '}
                  {d.show_date}
                </p>
              ) : null}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleOpenSubscription}
                className="w-full rounded-full border-brand-300 bg-white/70 text-brand-700 hover:bg-white"
              >
                Manage plan
              </Button>
            </CardContent>
          </Card>
        );
      })() : null}

      {/* Primary nav */}
      <nav>
        <ul className="space-y-1">
          {dashboardNav.map((item) => {
            const Icon = item.icon;
            const count = item.badge ? counts?.[item.badge] : undefined;
            return (
              <li key={`${item.label}-${item.path}`}>
                <NavLink
                  to={item.path}
                  end={item.path === '/dashboard'}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ink-2 transition-colors hover:bg-accent',
                      isActive && 'bg-accent text-brand-700'
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  <NavBadge count={count} />
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

        {/* Footer links */}
        <div className="mt-6 flex flex-wrap gap-x-3 gap-y-1 px-3 text-[0.65rem] text-muted-foreground">
          <Link to="/pricing.html" className="hover:text-ink">
            Pricing
          </Link>
          <Link to="/privacy-policy.html" className="hover:text-ink">
            Privacy
          </Link>
          <Link to="/terms-conditions.html" className="hover:text-ink">
            Terms
          </Link>
          <Link to="/refund-policy.html" className="hover:text-ink">
            Refunds
          </Link>
        </div>
      </div>

      {/* Subscription modal — opened by the Plan card's Manage button.
          Same lazy-mounted pattern as `UserMenu` so the PayPal/Cashfree
          chunks don't load until the user actually opens it. */}
      {subscriptionOpen ? (
        <Suspense fallback={null}>
          <SubscriptionDialog
            open={subscriptionOpen}
            onOpenChange={setSubscriptionOpen}
          />
        </Suspense>
      ) : null}
    </aside>
  );
}
